import { IProject, ProjectData } from '../api/IApiResponse';
import { ensureNestedStructure, ensureFlatStructure } from '../types/ProjectDataStructure';
import { unit as unitEnum } from '../enum/project';
import { normalizeProjectData } from './WireValueNormalizer';

/**
 * Railway load/traffic fields that are authoritative ONLY in mixed_train.
 * In single cargo_train / passenger_train the user edits the GENERIC designAxleLoad/trainSpeed/
 * wheelDiameter (and generic traffic fields); these suffixed + legacy-passenger fields keep their
 * stale defaults. Because submission merges the prior projectInfo.data, those hidden defaults
 * survive into the payload, where the backend treats them as authoritative (designAxleLoadCargo=500
 * overriding designAxleLoad=260 → wheel load 125 instead of 65). Scrubbed in single modes below.
 * Shared contract with macread-be: single modes carry their value only in the generic fields.
 */
const RAILWAY_MIXED_ONLY_LOAD_FIELDS = ['designAxleLoadCargo', 'trainSpeedCargo', 'wheelDiameterCargo', 'designAxleLoadPassenger', 'trainSpeedPassenger', 'wheelDiameterPassenger'];

const RAILWAY_LEGACY_PASSENGER_FIELDS = ['passengerAxleLoad', 'passengerTrainSpeed', 'passengerWheelDiameter'];

const RAILWAY_MIXED_ONLY_TRAFFIC_FIELDS = [
    'numberOfTrainsPerDayCargo',
    'numberOfCarsPerTrainCargo',
    'numberOfAxisPerCarCargo',
    'designLifeCargo',
    'numberOfTrainsPerDayPassenger',
    'numberOfCarsPerTrainPassenger',
    'numberOfAxisPerCarPassenger',
    'designLifePassenger',
];

/**
 * Service per preparare i payload per le chiamate al backend
 * Garantisce che i dati del progetto siano strutturati correttamente
 * evitando duplicazioni non necessarie
 *
 * Uses type-safe utilities from ProjectDataStructure.ts for FLAT → NESTED transformation
 */
export class BackendPayloadService {
    /**
     * @internal Use orchestratePayload() for external calls
     * @deprecated Direct external usage deprecated in Sprint 1.
     * Use orchestratePayload() instead which handles unit conversion + nesting.
     *
     * Prepara il payload per putDynamicProjectAsync
     * Mantiene solo i dati specifici del metodo in projectInfo.data[method]
     * e le proprietà comuni al livello root di projectInfo.data
     *
     * @param project - Progetto completo dal Redux state
     * @returns Payload ottimizzato per il backend
     */
    static prepareProjectPayload(project: IProject): IProject {
        if (!project?.guid) {
            throw new Error('Project GUID is required for backend payload');
        }

        const projectInfoData = project.projectInfo?.data;

        if (!projectInfoData) {
            throw new Error('Project data is required for backend payload');
        }

        // Reason: Geogrid projects have method='' and store data directly in projectInfo.data
        const isGeogridProject = !project.method || project.method === '';

        if (isGeogridProject) {
            // For Geogrid, return data as-is without nesting under method key
            return {
                ...project,
                projectInfo: {
                    ...(project.projectInfo || {}),
                    data: projectInfoData,
                },
            };
        }

        // Non-Geogrid projects: validate method and apply nesting logic
        if (!project.method) {
            throw new Error('Project method is required for backend payload');
        }

        const method = project.method;

        // 🔧 REFACTOR: Use type-safe ensureNestedStructure() from ProjectDataStructure.ts
        // REASON: Consolidate FLAT → NESTED transformation logic in single source of truth
        // This handles both flat and nested input data correctly with proper type guards
        //
        // Backend expects NESTED format: { type, notes, [method]: { ...fields } }
        // Redux stores FLAT format: { type, notes, ...fields, method }
        // ensureNestedStructure() transforms FLAT → NESTED preserving common properties
        const cleanProjectInfoData = ensureNestedStructure(projectInfoData, method, project.type);

        // Costruisci il payload finale
        const payload: IProject = {
            ...project,
            projectInfo: {
                ...(project.projectInfo || {}),
                data: cleanProjectInfoData as ProjectData,
            },
        };

        return payload;
    }

    /**
     * RUN guard (run-text-fields C3/C4): validates the ORIGINAL values of a RUN candidate before any
     * transformation, copy or request. Throws WireValueError (field path + reason); resolves to the normalized
     * copy of projectInfo.data (same FLAT/NESTED shape, numbers parsed, no unit conversion).
     */
    static async assertRunPayload(project: IProject): Promise<ProjectData | undefined> {
        const data = project.projectInfo?.data;
        if (!data) return undefined;
        const { resolveProjectSchemas } = await import('./ProjectTypeService');
        const type = project.type ?? '';
        const method = project.method ?? '';
        const schemas = resolveProjectSchemas(type, method);
        const normalized = normalizeProjectData(data as unknown as Record<string, unknown>, { projectType: type, method, direction: 'outbound', ...schemas }, 'strict') as unknown as ProjectData;
        if (type === 'railways' && schemas.schema) {
            const { ValidationService } = await import('./ValidationService');
            const candidate = ensureFlatStructure(data, method, type);
            const validation = ValidationService.generateValidationSchema(schemas.schema, unitEnum.SI);
            await validation.validateAt('materialTypes.ballastType', candidate);
            await validation.validateAt('soilProperties.cbrOfBallast', candidate);
        }
        return normalized;
    }

    /**
     * Orchestrates the complete Redux → Backend transformation pipeline
     *
     * Pipeline steps:
     * 1. Validate input (GUID, projectInfo.data required)
     * 2. Convert units if Imperial && !conversionApplied
     * 3. Apply FLAT → NESTED transformation
     * 4. Return payload ready for API call
     *
     * @param project - Project from Redux state (FLAT structure)
     * @returns Promise<IProject> - Project ready for backend (NESTED, Metric)
     */
    static async orchestratePayload(project: IProject): Promise<IProject> {
        // Step 1: Validate (reuse existing validation)
        if (!project?.guid) {
            throw new Error('Project GUID is required for backend payload');
        }
        if (!project.projectInfo?.data) {
            throw new Error('Project data is required for backend payload');
        }

        let workingProject = { ...project };

        // Step 1.5: Normalize wire values by schema before any unit conversion (run-text-fields C7)
        const { resolveProjectSchemas } = await import('./ProjectTypeService');
        workingProject = {
            ...workingProject,
            projectInfo: {
                ...project.projectInfo,
                data: normalizeProjectData(
                    project.projectInfo.data as unknown as Record<string, unknown>,
                    { projectType: project.type ?? '', method: project.method ?? '', direction: 'outbound', ...resolveProjectSchemas(project.type ?? '', project.method ?? '') },
                    'preserve'
                ) as unknown as ProjectData,
            },
        };
        const { method, type, unit } = project;

        // Step 2: Unit conversion (only if Imperial AND not already converted)
        const needsConversion =
            unit === 'Imperial' &&
            !project.conversionApplied &&
            method &&
            type;

        console.log('📐 [UNIT] orchestratePayload - Unit Check:', {
            guid: project.guid,
            unit,
            conversionApplied: project.conversionApplied,
            needsConversion,
            method,
            type,
        });

        if (needsConversion) {
            try {
                // Dynamic import to avoid circular dependencies
                const { convertFlatData } = await import('./UnitConversionService');
                const ProjectTypeService = await import('./ProjectTypeService');

                const projectTypeJson = await ProjectTypeService.getProjectTypeJson({
                    type: type!,
                    method: method!
                });
                const flatFields = Object.values(projectTypeJson.fields).flat();

                const convertedData = convertFlatData(
                    workingProject.projectInfo.data as Record<string, any>,
                    flatFields,
                    unitEnum.Imperial,
                    unitEnum.SI
                );

                if (convertedData) {
                    console.log('✅ [UNIT] Imperial→SI Conversion APPLIED:', {
                        guid: project.guid,
                        originalUnit: unitEnum.Imperial,
                        targetUnit: unitEnum.SI,
                        sampleConvertedKeys: Object.keys(convertedData).slice(0, 10),
                    });

                    workingProject = {
                        ...workingProject,
                        conversionApplied: true,
                        originalUnit: unitEnum.Imperial, // Track original unit system for debugging
                        projectInfo: {
                            ...workingProject.projectInfo,
                            data: convertedData as ProjectData,
                        },
                    };
                }
            } catch (error) {
                console.error('[BackendPayloadService] Unit conversion failed:', error);
                throw new Error('Unit conversion failed during payload orchestration');
            }
        }

        // Step 2.5: Normalize subgrade fields — keep only the active input method's value
        // BUG FIX: Paved/Airport forms store both cbrSubgrade and ev2Subgrade in the subgrade group,
        // but the toggle (subgradeInputMethod) lives in roadInfo. The inactive branch stays stale.
        // If both are sent to the backend, on reload the stale Ev2 value may override CBR (e.g., 45/10=4.5).
        const projectData = workingProject.projectInfo?.data as unknown as Record<string, unknown> | undefined;
        const roadInfoData = projectData?.roadInfo as Record<string, unknown> | undefined;
        const inputMethod = roadInfoData?.subgradeInputMethod;

        if (projectData?.subgrade && inputMethod) {
            // Deep-copy subgrade to avoid mutating Redux state on metric (non-converting) saves
            const cleanedSubgrade = { ...(projectData.subgrade as Record<string, unknown>) };
            if (inputMethod === 'cbr' || inputMethod === 'CBR') {
                delete cleanedSubgrade.ev2Subgrade;
            } else if (inputMethod === 'ev2' || inputMethod === 'modulus' || inputMethod === 'Ev2') {
                delete cleanedSubgrade.cbrSubgrade;
            }
            projectData.subgrade = cleanedSubgrade;
        }

        // Step 2.6: Normalize railway mode-specific load/traffic fields — keep only the active mode's values.
        // BUG FIX (wheel load): in single cargo_train/passenger_train the user edits the GENERIC
        // designAxleLoad/trainSpeed/wheelDiameter, but the mixed-only *Cargo/*Passenger (and legacy
        // passenger*) fields keep their defaults. Submission merges the prior projectInfo.data, so those
        // stale hidden values survive into the payload and the backend treats them as authoritative
        // (designAxleLoadCargo=500 overriding designAxleLoad=260 → wheel load 125 instead of 65).
        // showWhen visibility does NOT scrub them — this does. mixed_train keeps its real per-mode inputs.
        // NOTE: this scrubs the FLAT shape (Redux is always FLAT per PD3/PD4, before prepareProjectPayload
        // nests it under the method key). An already-nested default.loadData would bypass this, but the
        // backend B2 mode-normalizer nulls mode-specific fields server-side too (defense-in-depth).
        const railwayTypeData = projectData?.railwayType as Record<string, unknown> | undefined;
        const trainMode =
            (railwayTypeData?.railwayToDesign as string | undefined) ??
            (projectData?.railwayToDesign as string | undefined) ??
            ((projectData?.loadData as Record<string, unknown> | undefined)?.railwayToDesign as string | undefined);
        // Normalize so saved/imported variants (MIXED_TRAIN, 'mixed train', 'MIXED TRAIN') all map to
        // 'mixed_train' and are NOT mis-classified as single — which would wrongly scrub real mixed inputs.
        // Mirrors the backend's NormalizeTrainType tolerance.
        const normalizedTrainMode = trainMode ? trainMode.trim().toLowerCase().replace(/\s+/g, '_') : undefined;
        const isSingleTrainMode = !!normalizedTrainMode && normalizedTrainMode !== 'mixed_train';

        if (projectData && isSingleTrainMode) {
            const loadData = projectData.loadData as Record<string, unknown> | undefined;
            if (loadData) {
                // Deep-copy to avoid mutating Redux state on metric (non-converting) saves
                const cleanedLoadData = { ...loadData };
                [...RAILWAY_MIXED_ONLY_LOAD_FIELDS, ...RAILWAY_LEGACY_PASSENGER_FIELDS].forEach(field => {
                    delete cleanedLoadData[field];
                });
                projectData.loadData = cleanedLoadData;
            }

            const trafficData = projectData.trafficData as Record<string, unknown> | undefined;
            if (trafficData) {
                const cleanedTrafficData = { ...trafficData };
                RAILWAY_MIXED_ONLY_TRAFFIC_FIELDS.forEach(field => {
                    delete cleanedTrafficData[field];
                });
                projectData.trafficData = cleanedTrafficData;
            }
        }

        // Step 3: Apply FLAT → NESTED transformation
        console.log('🔄 [FLAT/NESTED] Redux→Backend BEFORE prepareProjectPayload:', {
            guid: workingProject.guid,
            method: workingProject.method,
            unit: workingProject.unit,
            dataKeys: Object.keys(workingProject.projectInfo?.data || {}).slice(0, 15),
            structure: 'FLAT (from Redux)',
        });

        const nestedPayload = this.prepareProjectPayload(workingProject);

        console.log('✅ [FLAT/NESTED] Redux→Backend AFTER prepareProjectPayload:', {
            guid: nestedPayload.guid,
            method: nestedPayload.method,
            dataKeys: Object.keys(nestedPayload.projectInfo?.data || {}),
            hasMethodKey: nestedPayload.method && (nestedPayload.projectInfo?.data as any)?.[nestedPayload.method] !== undefined,
            structure: nestedPayload.method && (nestedPayload.projectInfo?.data as any)?.[nestedPayload.method] ? 'NESTED (for backend)' : 'FLAT (geogrid)',
        });

        return nestedPayload;
    }

    /**
     * Verifica se il payload contiene duplicazioni di dati
     * Utile per debugging e test
     *
     * @param project - Progetto da verificare
     * @returns Informazioni sulle duplicazioni trovate
     */
    static analyzeDataDuplication(project: IProject): {
        hasDuplication: boolean;
        duplicatedFields: string[];
        sizeBefore: number;
        sizeAfter: number;
        compressionRatio: number;
    } {
        if (!project?.projectInfo?.data || !project?.method) {
            return {
                hasDuplication: false,
                duplicatedFields: [],
                sizeBefore: 0,
                sizeAfter: 0,
                compressionRatio: 1,
            };
        }

        const originalData = project.projectInfo.data;
        const method = project.method;
        const methodData = (originalData as any)[method];

        if (!methodData) {
            return {
                hasDuplication: false,
                duplicatedFields: [],
                sizeBefore: 0,
                sizeAfter: 0,
                compressionRatio: 1,
            };
        }

        const originalSize = JSON.stringify(originalData).length;
        const cleanPayload = this.prepareProjectPayload(project);
        const cleanSize = JSON.stringify(cleanPayload.projectInfo.data).length;

        // Trova i campi duplicati (presenti sia al root che nel method)
        const duplicatedFields: string[] = [];
        const methodKeys = Object.keys(methodData);
        const rootKeys = Object.keys(originalData);

        methodKeys.forEach(key => {
            if (rootKeys.includes(key) && key !== 'type') {
                // 'type' può essere legittimamente in entrambi
                duplicatedFields.push(key);
            }
        });

        return {
            hasDuplication: duplicatedFields.length > 0,
            duplicatedFields,
            sizeBefore: originalSize,
            sizeAfter: cleanSize,
            compressionRatio: originalSize > 0 ? cleanSize / originalSize : 1,
        };
    }
}

export default BackendPayloadService;
